import { crewDefaults, humanDefaults, occupiedTellDefaults } from '../../../lib/s2/defaults';
import {
  OCCUPIED_DISPOSITIONS,
  OCCUPIED_GATES,
  type CrewDef,
  type CrewMemberDef,
  type HumanDef,
  type OccupiedTellDef,
} from '../../../lib/s2/types';
import { withDefaults } from '../../../lib/teamsDefaults';
import {
  CheckboxField,
  Field,
  Fieldset,
  ItemPicker,
  IssueList,
  ListEditor,
  NumberField,
  NumberInput,
  SelectField,
  SelectInput,
  StringListEditor,
  TextField,
} from '../fields';
import { CastLookField } from '../CastLookField';
import { useContentBundle, useContentRefs } from '../refs';
import type { FormProps } from '../types';
import { KeyValueEditor } from './kv';

/**
 * A crew (occupied.html): who lives in a building, by OSM tags. `dispositions` weights should sum to 100; `headcount` empty means
 * the roster size; `priceMult` 0 is the rules' `fairPriceMult`.
 */
export function CrewForm({ value, onChange, errors }: FormProps<CrewDef>) {
  const c = withDefaults(crewDefaults(), value ?? {}) as Required<CrewDef>;
  const set = <K extends keyof CrewDef>(k: K, v: CrewDef[K]) => onChange({ ...c, [k]: v });
  const bundle = useContentBundle();
  const { lootTables } = useContentRefs();
  const humans = (bundle.humans ?? []).map((h) => ({ value: h.id ?? '', label: h.id ?? '' }));
  // Recruit roles are retired (card 11): a crew member's recruit kind is a background id.
  const roles = (bundle.backgrounds ?? []).map((g) => ({ value: g?.id ?? '', label: g?.id ?? '' }));
  const tables = lootTables.map((t) => ({ value: t.id ?? '', label: t.id ?? '' }));
  const sum = Object.values(c.dispositions).reduce((a, n) => a + (n ?? 0), 0);
  const useRoster = c.headcount.length === 0;
  const hc = [c.headcount[0] ?? 1, c.headcount[1] ?? 1];

  return (
    <>
      <Fieldset legend="Crew">
        <TextField label="Id" mono value={c.id} issues={errors.id} onChange={(v) => set('id', v)} />
        <TextField
          label="Name"
          value={c.name}
          issues={errors.name}
          onChange={(v) => set('name', v)}
        />
        <StringListEditor
          label="OSM tags"
          base="osmTags"
          hint="e.g. shop=agrarian, amenity=restaurant+cuisine=pizza"
          value={c.osmTags}
          errors={errors}
          onChange={(v) => set('osmTags', v)}
        />
        <NumberField
          label="Price multiplier"
          hint="0 = the rules' fair price multiplier."
          min={0}
          value={c.priceMult}
          issues={errors.priceMult}
          onChange={(v) => set('priceMult', v ?? 0)}
        />
        <SelectField
          label="Trade table"
          none="(none)"
          value={c.tradeTableId}
          options={tables}
          issues={errors.tradeTableId}
          onChange={(v) => set('tradeTableId', v ?? '')}
        />
        <SelectField
          label="Stash table"
          none="(none)"
          value={c.stashTableId}
          options={tables}
          issues={errors.stashTableId}
          onChange={(v) => set('stashTableId', v ?? '')}
        />
      </Fieldset>
      <Fieldset legend="Dispositions and headcount">
        <KeyValueEditor
          label="Dispositions"
          base="dispositions"
          kind="number"
          keys={OCCUPIED_DISPOSITIONS}
          hint="Weights, whole numbers. They should add up to 100."
          value={c.dispositions}
          errors={errors}
          onChange={(v) => set('dispositions', v)}
        />
        <p
          data-testid="disposition-sum"
          className={`text-xs sm:col-span-2 ${sum === 100 || sum === 0 ? 'text-bruise' : 'text-blam'}`}
        >
          Sums to {sum}
          {sum === 100 ? '' : ' (should be 100)'}
        </p>
        <div className="sm:col-span-2">
          <CheckboxField
            label="Use roster size"
            checked={useRoster}
            hint="Headcount empty = as many humans as the roster has."
            onChange={(on) => {
              const n = Math.max(1, c.roster.length);
              set('headcount', on ? [] : [n, n]);
            }}
          />
          {!useRoster && (
            <div className="mt-1 grid grid-cols-2 gap-2">
              <Field label="Headcount min" issues={errors['headcount.0']}>
                {(id, invalid) => (
                  <NumberInput
                    id={id}
                    integer
                    min={0}
                    invalid={invalid}
                    value={hc[0]}
                    onChange={(v) => set('headcount', [v ?? 0, hc[1] ?? 0])}
                  />
                )}
              </Field>
              <Field label="Headcount max" issues={errors['headcount.1']}>
                {(id, invalid) => (
                  <NumberInput
                    id={id}
                    integer
                    min={0}
                    invalid={invalid}
                    value={hc[1]}
                    onChange={(v) => set('headcount', [hc[0] ?? 0, v ?? 0])}
                  />
                )}
              </Field>
            </div>
          )}
          <IssueList issues={errors.headcount} />
        </div>
      </Fieldset>
      <Fieldset legend="Roster">
        <ListEditor<CrewMemberDef>
          label="Roster"
          what="member"
          items={c.roster}
          issues={errors.roster}
          onChange={(v) => set('roster', v)}
          make={() => ({ archetype: '', name: '', outfit: '', recruitRoleId: '' })}
          row={(m, j, setM) => (
            <div className="grid gap-2 sm:grid-cols-2">
              <Field label="Archetype" issues={errors[`roster.${j}.archetype`]}>
                {(id, invalid) => (
                  <SelectInput
                    id={id}
                    invalid={invalid}
                    value={m.archetype}
                    options={humans}
                    onChange={(v) => setM({ ...m, archetype: v ?? '' })}
                  />
                )}
              </Field>
              <TextField
                label="Name"
                value={m.name}
                issues={errors[`roster.${j}.name`]}
                onChange={(v) => setM({ ...m, name: v })}
              />
              <TextField
                label="Outfit"
                value={m.outfit}
                issues={errors[`roster.${j}.outfit`]}
                onChange={(v) => setM({ ...m, outfit: v })}
              />
              <Field
                label="Recruit role"
                issues={errors[`roster.${j}.recruitRoleId`]}
                hint="Never = can't be recruited."
              >
                {(id, invalid) => (
                  <SelectInput
                    id={id}
                    invalid={invalid}
                    none="(never)"
                    value={m.recruitRoleId}
                    options={roles}
                    onChange={(v) => setM({ ...m, recruitRoleId: v ?? '' })}
                  />
                )}
              </Field>
              <CastLookField
                value={(m as { appearance?: string | null }).appearance}
                issues={errors[`roster.${j}.appearance`]}
                onChange={(v) => setM({ ...m, appearance: v } as CrewMemberDef)}
              />
            </div>
          )}
        />
      </Fieldset>
      <Fieldset legend="Lines">
        <KeyValueEditor
          label="Lines"
          base="lines"
          kind="string"
          multiline
          hint="What the crew says, by key."
          value={c.lines}
          errors={errors}
          onChange={(v) => set('lines', v)}
        />
      </Fieldset>
    </>
  );
}

type NumOpts = { integer?: boolean; min?: number; max?: number; hint?: string };

/** A human archetype (occupied.html): body, weapon, aim and senses. Absent fields show the C# defaults. */
export function HumanForm({ value, onChange, errors }: FormProps<HumanDef>) {
  const h = withDefaults(humanDefaults(), value ?? {}) as Required<HumanDef>;
  const set = <K extends keyof HumanDef>(k: K, v: HumanDef[K]) => onChange({ ...h, [k]: v });
  const num = (k: keyof HumanDef & string, label: string, o: NumOpts = {}) => (
    <NumberField
      key={k}
      label={label}
      hint={o.hint}
      integer={o.integer}
      min={o.min ?? 0}
      max={o.max}
      value={h[k] as number}
      issues={errors[k]}
      onChange={(v) => set(k, (v ?? 0) as never)}
    />
  );
  return (
    <>
      <Fieldset legend="Human">
        <TextField label="Id" mono value={h.id} issues={errors.id} onChange={(v) => set('id', v)} />
      </Fieldset>
      <Fieldset legend="Body">
        {num('health', 'Health', { integer: true, min: 1 })}
        {num('morale', 'Morale', { integer: true })}
        {num('moraleFloor', 'Morale floor', { integer: true })}
        {num('headshotMult', 'Headshot multiplier')}
        {num('knockbackResist', 'Knockback resist', { max: 1 })}
        <CheckboxField
          label="Head armor"
          checked={h.headArmor}
          onChange={(v) => set('headArmor', v)}
        />
        <Field label="Armor" issues={errors.armorId} hint="Empty = none.">
          {(id, invalid) => (
            <ItemPicker
              id={id}
              invalid={invalid}
              none="(none)"
              filter={(c) => c === 'armor'}
              value={h.armorId}
              onChange={(v) => set('armorId', v ?? '')}
            />
          )}
        </Field>
      </Fieldset>
      <Fieldset legend="Weapon">
        <Field label="Weapon" issues={errors.weaponId} hint="Empty = unarmed (melee).">
          {(id, invalid) => (
            <ItemPicker
              id={id}
              invalid={invalid}
              none="(unarmed)"
              filter={(c) => c === 'weapon'}
              value={h.weaponId}
              onChange={(v) => set('weaponId', v ?? '')}
            />
          )}
        </Field>
        {num('meleeDamage', 'Melee damage', { integer: true })}
        <CheckboxField
          label="Gun shots wound (WIND)"
          hint="A landed gun shot is a wound; off = a hit (wind). A melee strike is always a hit."
          checked={h.weaponWounds ?? true}
          onChange={(v) => set('weaponWounds', v)}
        />
      </Fieldset>
      <Fieldset legend="Aim">
        {num('telegraphS', 'Telegraph (s)')}
        {num('fireIntervalS', 'Fire interval (s)')}
        {num('hitNear', 'Hit chance near', { max: 1 })}
        {num('nearM', 'Near (m)')}
        {num('hitFar', 'Hit chance far', { max: 1 })}
        {num('farM', 'Far (m)')}
      </Fieldset>
      <Fieldset legend="Senses and movement">
        {num('sightHalfAngleDeg', 'Sight half angle (deg)')}
        {num('sightRangeM', 'Sight range (m)')}
        {num('walkMps', 'Walk (m/s)')}
        {num('runMps', 'Run (m/s)')}
        <CheckboxField
          label="Seeks cover"
          checked={h.coverSeek}
          onChange={(v) => set('coverSeek', v)}
        />
      </Fieldset>
    </>
  );
}

/** A tell (occupied.html): a clue the player can notice, at a gate, for a disposition. */
export function OccupiedTellForm({ value, onChange, errors }: FormProps<OccupiedTellDef>) {
  const t = withDefaults(occupiedTellDefaults(), value ?? {}) as Required<OccupiedTellDef>;
  const set = <K extends keyof OccupiedTellDef>(k: K, v: OccupiedTellDef[K]) =>
    onChange({ ...t, [k]: v });
  const opts = (xs: readonly string[]) => xs.map((x) => ({ value: x, label: x }));
  return (
    <Fieldset legend="Tell">
      <TextField label="Id" mono value={t.id} issues={errors.id} onChange={(v) => set('id', v)} />
      <SelectField
        label="Gate"
        value={t.gate}
        options={opts(OCCUPIED_GATES)}
        issues={errors.gate}
        onChange={(v) => set('gate', v ?? '')}
      />
      <SelectField
        label="Disposition"
        value={t.disposition}
        options={opts(OCCUPIED_DISPOSITIONS)}
        issues={errors.disposition}
        onChange={(v) => set('disposition', v ?? '')}
      />
      <CheckboxField
        label="Map"
        checked={t.map}
        hint="Far tell, yard tells only."
        onChange={(v) => set('map', v)}
      />
      <TextField
        label="Panel prop"
        mono
        value={t.panelProp}
        issues={errors.panelProp}
        onChange={(v) => set('panelProp', v)}
      />
      <TextField
        label="Text"
        multiline
        value={t.text}
        issues={errors.text}
        onChange={(v) => set('text', v)}
      />
    </Fieldset>
  );
}
